import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import {
  getEvents,
  registerForEvent,
  unregisterFromEvent,
  verifyAttendanceOtp,
} from "../services/eventService";
import { getMyMembership } from "../services/membershipService";
import "./events.css";

const STATUS_SECTIONS = ["upcoming", "ongoing", "completed"];

const STATUS_CONFIG = {
  upcoming: { label: "Upcoming", icon: "→", color: "#fbbf24" },
  ongoing: { label: "Happening Now", icon: "●", color: "#60a5fa" },
  completed: { label: "Past Events", icon: "✓", color: "#10b981" },
};

const Events = () => {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [otpInputs, setOtpInputs] = useState({});
  const [loading, setLoading] = useState(true);
  const [canAccessEvents, setCanAccessEvents] = useState(false);

  const loadEvents = async () => {
    try {
      const data = await getEvents();
      setEvents(data);
    } catch (err) {
      alert(err.message || "Unable to load events");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const loadMembershipAndEvents = async () => {
      try {
        const membership = await getMyMembership();
        const isApproved = membership?.status === "approved";
        const isValid = membership?.validTill
          ? new Date(membership.validTill) >= new Date()
          : false;

        if (!isApproved || !isValid) {
          alert("Approved membership is required to access events.");
          navigate("/membership", { replace: true });
          return;
        }

        setCanAccessEvents(true);
        await loadEvents();
      } catch (err) {
        alert(err.message || "Please complete membership to access events.");
        navigate("/membership", { replace: true });
      }
    };

    loadMembershipAndEvents();
  }, [navigate]);

  const groupedEvents = useMemo(() => {
    return STATUS_SECTIONS.reduce((acc, key) => {
      acc[key] = events.filter((event) => event.status === key);
      return acc;
    }, {});
  }, [events]);

  const onRegister = async (eventId) => {
    try {
      await registerForEvent(eventId);
      await loadEvents();
      alert("Registered successfully");
    } catch (err) {
      alert(err.message || "Registration failed");
    }
  };

  const onUnregister = async (eventId) => {
    if (!window.confirm("Are you sure you want to unregister from this event?")) return;
    try {
      await unregisterFromEvent(eventId);
      await loadEvents();
      alert("Unregistered successfully");
    } catch (err) {
      alert(err.message || "Unregistration failed");
    }
  };

  const onVerifyOtp = async (eventId) => {
    try {
      await verifyAttendanceOtp(eventId, otpInputs[eventId] || "");
      await loadEvents();
      alert("Attendance marked as present");
    } catch (err) {
      alert(err.message || "OTP verification failed");
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut",
      },
    },
  };

  const cardHoverVariants = {
    rest: { y: 0 },
    hover: { y: -8, transition: { duration: 0.3 } },
  };

  return (
    <>
      <Navbar />

      <div className="events-page">
        <div className="events-atmosphere">
          <div className="glow-orb glow-orb-1" />
          <div className="glow-orb glow-orb-2" />
          <div className="glow-orb glow-orb-3" />
        </div>

        <motion.div
          className="events-container"
          initial="hidden"
          animate="visible"
          variants={containerVariants}
        >
          {/* Hero Section */}
          <motion.section className="events-hero" variants={itemVariants}>
            <div className="hero-content">
              <motion.div
                className="hero-accent-line"
                initial={{ scaleX: 0, opacity: 0 }}
                animate={{ scaleX: 1, opacity: 1 }}
                transition={{ duration: 0.8, delay: 0.1 }}
              />
              <motion.h1
                className="hero-title"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
              >
                Where
                <br />
                <span className="hero-gradient">Innovation</span>
                <br />
                Happens
              </motion.h1>
              <motion.p
                className="hero-subtitle"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
              >
                Discover workshops, innovation challenges, networking experiences, and leadership sessions inside YUVA. Join the community where ambitious people gather, build, and transform ideas into reality.
              </motion.p>
            </div>
          </motion.section>

          {/* Events Sections */}
          {!canAccessEvents ? (
            <motion.div
              className="access-check"
              variants={itemVariants}
            >
              <div className="pulse-loader" />
              <p>Verifying your access...</p>
            </motion.div>
          ) : loading ? (
            <motion.div
              className="loading-state"
              variants={itemVariants}
            >
              <div className="pulse-loader" />
              <p>Preparing your events ecosystem...</p>
            </motion.div>
          ) : (
            <motion.div
              className="status-sections-container"
              variants={containerVariants}
              initial="hidden"
              animate="visible"
            >
              {STATUS_SECTIONS.map((status, statusIdx) => (
                <motion.section
                  key={status}
                  className="event-section"
                  variants={itemVariants}
                  custom={statusIdx}
                  initial="hidden"
                  animate="visible"
                >
                  <div className="section-header">
                    <div className="section-title-wrap">
                      <span
                        className="section-icon"
                        style={{ color: STATUS_CONFIG[status].color }}
                      >
                        {STATUS_CONFIG[status].icon}
                      </span>
                      <h2 className="section-title">
                        {STATUS_CONFIG[status].label}
                      </h2>
                    </div>
                    {groupedEvents[status].length > 0 && (
                      <span className="event-count">
                        {groupedEvents[status].length} event{groupedEvents[status].length !== 1 ? "s" : ""}
                      </span>
                    )}
                  </div>

                  <div className="section-divider" />

                  {groupedEvents[status].length === 0 ? (
                    <motion.div
                      className="empty-state-premium"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ duration: 0.6 }}
                    >
                      <div className="empty-icon">✨</div>
                      <h3>New Experiences Being Prepared</h3>
                      <p>
                        {status === "upcoming"
                          ? "Exciting events are coming soon. Stay tuned!"
                          : status === "ongoing"
                          ? "No events happening right now. Check back soon!"
                          : "No completed events yet."}
                      </p>
                    </motion.div>
                  ) : (
                    <motion.div
                      className="event-grid"
                      variants={containerVariants}
                      initial="hidden"
                      animate="visible"
                    >
                      {groupedEvents[status].map((event, idx) => (
                        <EventCard
                          key={event._id}
                          event={event}
                          status={status}
                          statusConfig={STATUS_CONFIG[status]}
                          itemVariants={itemVariants}
                          cardHoverVariants={cardHoverVariants}
                          otpInputs={otpInputs}
                          setOtpInputs={setOtpInputs}
                          onRegister={onRegister}
                          onUnregister={onUnregister}
                          onVerifyOtp={onVerifyOtp}
                          idx={idx}
                        />
                      ))}
                    </motion.div>
                  )}
                </motion.section>
              ))}
            </motion.div>
          )}
        </motion.div>
      </div>

      <Footer />
    </>
  );
};

const EventCard = ({
  event,
  status,
  statusConfig,
  itemVariants,
  cardHoverVariants,
  otpInputs,
  setOtpInputs,
  onRegister,
  onUnregister,
  onVerifyOtp,
  idx,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const eventDate = new Date(event.date);
  const isCompleted = status === "completed";

  return (
    <motion.div
      variants={itemVariants}
      custom={idx}
      initial="hidden"
      animate="visible"
      className="event-card-wrapper"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <motion.article
        className="event-card"
        variants={cardHoverVariants}
        initial="rest"
        animate={isHovered ? "hover" : "rest"}
      >
        {/* Card Background Glow */}
        <div className="card-glow-bg" />

        {/* Event Poster */}
        {event.poster && (
          <motion.div
            className="event-poster-section"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.3 }}
          >
            <img src={event.poster} alt={event.title} className="event-poster" />
            <div className="poster-overlay" />
          </motion.div>
        )}

        {/* Card Content */}
        <div className="card-content">
          {/* Header */}
          <div className="card-header">
            <div className="card-meta">
              <span
                className="status-dot"
                style={{
                  backgroundColor:
                    status === "upcoming"
                      ? "#fbbf24"
                      : status === "ongoing"
                      ? "#60a5fa"
                      : "#10b981",
                }}
              />
              <span className="event-date">
                {eventDate.toLocaleDateString("en-US", {
                  month: "short",
                  day: "numeric",
                })}
              </span>
            </div>
            <span className="status-badge" data-status={status}>
              {status}
            </span>
          </div>

          {/* Title */}
          <motion.h3
            className="card-title"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            {event.title}
          </motion.h3>

          {/* Description */}
          <p className="card-description">{event.description}</p>

          {/* Event Details */}
          <div className="event-details">
            <div className="detail-item">
              <span className="detail-label">Time</span>
              <span className="detail-value">{event.time}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Location</span>
              <span className="detail-value">{event.location}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Status</span>
              <span className="detail-value">
                {event.attendanceStatus === "present" ? "✓ Present" : "— Not marked"}
              </span>
            </div>
          </div>

          {/* Actions */}
          <div className="card-actions">
            {!event.isRegistered ? (
              <motion.button
                className="action-btn primary"
                disabled={isCompleted}
                onClick={() => onRegister(event._id)}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <span className="btn-icon">→</span>
                {isCompleted ? "Event Completed" : "Register Now"}
              </motion.button>
            ) : (
              <div className="registered-state-container">
                <div className="registered-state">
                  <span className="registered-badge">✓ Registered</span>
                </div>
                {!isCompleted && event.attendanceStatus !== "present" && (
                  <motion.button
                    className="action-btn secondary unregister-btn"
                    onClick={() => onUnregister(event._id)}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    Unregister
                  </motion.button>
                )}
              </div>
            )}

            {event.isRegistered && event.attendanceStatus !== "present" && (
              <motion.div
                className="otp-input-section"
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                transition={{ duration: 0.3 }}
              >
                <input
                  type="text"
                  placeholder="Enter IN OTP"
                  value={otpInputs[event._id] || ""}
                  onChange={(e) =>
                    setOtpInputs((prev) => ({
                      ...prev,
                      [event._id]: e.target.value,
                    }))
                  }
                  className="otp-input"
                />
                <motion.button
                  className="action-btn secondary"
                  onClick={() => onVerifyOtp(event._id)}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Verify
                </motion.button>
              </motion.div>
            )}
          </div>
        </div>

        {/* Corner Accent */}
        <div className="card-corner-accent" />
      </motion.article>
    </motion.div>
  );
};

export default Events;
